import companyLogoManifestSource from "./companyLogoManifest.generated.json";
import companyLogoPresetManifestSource from "./companyLogoPresetManifest.generated.json";
import {
  parseCompanyLogoManifest,
  resolveCompanyLogo,
} from "./companyLogoManifest";

export const TICKER_PIXEL_PATTERN_SIZE = 7;

export type TickerPixel = "." | "1" | "2" | "3" | "4";

export interface TickerIdentity {
  /** Uppercase ticker used as the stable lookup key. */
  ticker: string;
  /** Human-readable fallback symbol name. */
  symbolName: string;
  /** Seven rows of seven pixels. `.` is transparent; `1`–`4` map to the palette. */
  pixelPattern: readonly string[];
  foregroundColor: string;
  backgroundColor: string;
  secondaryColor: string;
  highlightColor: string;
  inkColor: string;
  /** Lets renderers distinguish a curated mark from a deterministic fallback. */
  isCurated: boolean;
  /**
   * Optional, reviewed local asset override for a future licensed SVG/PNG.
   * Renderers must keep `pixelPattern` as the dependency-free fallback.
   */
  assetPath?: string;
  /** Minimal runtime metadata for a reviewed company or fund-sponsor logo. */
  logoAsset?: {
    entityName: string;
    relationship: "issuer" | "fund-sponsor" | "other";
    relationshipNote: string | null;
    sourceProvider: string;
    licenseName: string;
  };
}

type CuratedTickerIdentity = Omit<TickerIdentity, "ticker" | "isCurated">;

const CURATED_TICKER_IDENTITIES = {
  AAPL: {
    symbolName: "과수 코어",
    backgroundColor: "#E8E4D8",
    foregroundColor: "#4A5555",
    secondaryColor: "#7ABF63",
    highlightColor: "#F7F3E5",
    inkColor: "#263333",
    pixelPattern: [
      "...22..",
      "..221..",
      "..111..",
      ".11311.",
      ".11111.",
      "..111..",
      "...1...",
    ],
  },
  MSFT: {
    symbolName: "작업창",
    backgroundColor: "#D9EFF2",
    foregroundColor: "#2E739D",
    secondaryColor: "#E9AA3E",
    highlightColor: "#F5FAE9",
    inkColor: "#183F58",
    pixelPattern: [
      ".11111.",
      ".14441.",
      ".14231.",
      ".14441.",
      ".13331.",
      ".11111.",
      "...1...",
    ],
  },
  NVDA: {
    symbolName: "네온 칩",
    backgroundColor: "#DDF2B6",
    foregroundColor: "#65A932",
    secondaryColor: "#243D27",
    highlightColor: "#F4FFD6",
    inkColor: "#15281A",
    pixelPattern: [
      "..121..",
      ".11111.",
      "1123211",
      "1134311",
      "1123211",
      ".11111.",
      "..121..",
    ],
  },
  TSLA: {
    symbolName: "번개 길",
    backgroundColor: "#F5D9D3",
    foregroundColor: "#D3423F",
    secondaryColor: "#F19855",
    highlightColor: "#FFF0D8",
    inkColor: "#672A2B",
    pixelPattern: [
      "....11.",
      "...113.",
      "..111..",
      ".112...",
      "..111..",
      "..11...",
      ".11....",
    ],
  },
  KO: {
    symbolName: "탄산 방울",
    backgroundColor: "#F7E5DB",
    foregroundColor: "#C84942",
    secondaryColor: "#E6AA4A",
    highlightColor: "#FFF8E3",
    inkColor: "#6E2928",
    pixelPattern: [
      "..3.3..",
      ".3...3.",
      "...2...",
      ".21112.",
      "2111112",
      ".21112.",
      "..222..",
    ],
  },
  SPY: {
    symbolName: "시장 나침반",
    backgroundColor: "#DCE8E5",
    foregroundColor: "#315E70",
    secondaryColor: "#D5A441",
    highlightColor: "#F8F2D5",
    inkColor: "#173A45",
    pixelPattern: [
      "...2...",
      "..212..",
      ".21412.",
      "2114112",
      ".21412.",
      "..212..",
      "...2...",
    ],
  },
} as const satisfies Record<string, CuratedTickerIdentity>;

export const CURATED_TICKERS = Object.freeze(
  Object.keys(CURATED_TICKER_IDENTITIES),
);

const FALLBACK_PALETTES = [
  {
    backgroundColor: "#E8E0C6",
    foregroundColor: "#547055",
    secondaryColor: "#C59242",
    highlightColor: "#FFF4CE",
    inkColor: "#283E30",
  },
  {
    backgroundColor: "#D9E6E8",
    foregroundColor: "#446D84",
    secondaryColor: "#B86555",
    highlightColor: "#F3F3D7",
    inkColor: "#243E48",
  },
  {
    backgroundColor: "#E8DDF0",
    foregroundColor: "#735B91",
    secondaryColor: "#4D876F",
    highlightColor: "#FFF0D1",
    inkColor: "#3F3152",
  },
  {
    backgroundColor: "#EEDCCF",
    foregroundColor: "#A85D46",
    secondaryColor: "#4D7C78",
    highlightColor: "#FFF1CF",
    inkColor: "#54372F",
  },
] as const;

function normalizeTicker(ticker: string): string {
  const normalized = ticker.trim().toUpperCase().replace(/[^A-Z0-9.-]/g, "");
  return normalized || "?";
}

function tickerHash(ticker: string): number {
  let hash = 2166136261;

  for (const character of ticker) {
    hash ^= character.codePointAt(0) ?? 0;
    hash = Math.imul(hash, 16777619);
  }

  return hash >>> 0;
}

function createFallbackPattern(hash: number): readonly string[] {
  const rows = [
    ".11111.",
    "1.....1",
    "1.....1",
    "1.....1",
    "1.....1",
    "1.....1",
    ".11111.",
  ].map((row) => row.split(""));

  // A mirrored 3×3 fingerprint gives unknown tickers a stable, unique badge.
  for (let y = 0; y < 3; y += 1) {
    for (let x = 0; x < 3; x += 1) {
      const bitIndex = y * 3 + x;
      const paletteIndex = ((hash >>> bitIndex) & 1) === 1 ? "2" : "3";
      rows[y + 2][x + 1] = paletteIndex;
      rows[y + 2][5 - x] = paletteIndex;
    }
  }

  rows[3][3] = "4";
  return rows.map((row) => row.join(""));
}

const fallbackCache = new Map<string, TickerIdentity>();
const companyLogoManifest = parseCompanyLogoManifest(
  companyLogoManifestSource,
);

interface CompanyLogoPreset {
  assetPath: string;
  entityName: string;
  relationship: "issuer" | "fund-sponsor" | "other";
  relationshipNote: string | null;
  sourceProvider: string;
  licenseName: string;
}

function parseCompanyLogoPresets(value: unknown): Map<string, CompanyLogoPreset> {
  if (
    typeof value !== "object" ||
    value === null ||
    !("schemaVersion" in value) ||
    value.schemaVersion !== 1 ||
    !("entries" in value) ||
    typeof value.entries !== "object" ||
    value.entries === null
  ) {
    return new Map();
  }

  const presets = new Map<string, CompanyLogoPreset>();
  for (const [ticker, candidate] of Object.entries(value.entries)) {
    if (
      typeof candidate !== "object" ||
      candidate === null ||
      !("assetPath" in candidate) ||
      typeof candidate.assetPath !== "string" ||
      !candidate.assetPath.startsWith("/company-logos/presets/") ||
      !("entityName" in candidate) ||
      typeof candidate.entityName !== "string" ||
      !("relationship" in candidate) ||
      (candidate.relationship !== "issuer" &&
        candidate.relationship !== "fund-sponsor" &&
        candidate.relationship !== "other") ||
      !("relationshipNote" in candidate) ||
      (candidate.relationshipNote !== null &&
        typeof candidate.relationshipNote !== "string") ||
      !("sourceProvider" in candidate) ||
      typeof candidate.sourceProvider !== "string" ||
      !("licenseName" in candidate) ||
      typeof candidate.licenseName !== "string"
    ) {
      continue;
    }
    presets.set(ticker.trim().toUpperCase(), {
      assetPath: candidate.assetPath,
      entityName: candidate.entityName,
      relationship: candidate.relationship,
      relationshipNote: candidate.relationshipNote,
      sourceProvider: candidate.sourceProvider,
      licenseName: candidate.licenseName,
    });
  }
  return presets;
}

const companyLogoPresets = parseCompanyLogoPresets(
  companyLogoPresetManifestSource,
);

function resolveReviewedLogo(ticker: string) {
  const entry = resolveCompanyLogo(companyLogoManifest, { ticker });
  if (entry) {
    return {
      assetPath: entry.asset.path,
      logoAsset: {
        entityName: entry.entityName,
        relationship: entry.relationship,
        relationshipNote: entry.relationshipNote,
        sourceProvider: entry.source.provider,
        licenseName: entry.license.name,
      },
    } satisfies Partial<TickerIdentity>;
  }

  const preset = companyLogoPresets.get(ticker);
  if (!preset) return {};

  return {
    assetPath: preset.assetPath,
    logoAsset: {
      entityName: preset.entityName,
      relationship: preset.relationship,
      relationshipNote: preset.relationshipNote,
      sourceProvider: preset.sourceProvider,
      licenseName: preset.licenseName,
    },
  } satisfies Partial<TickerIdentity>;
}

export function getTickerIdentity(ticker: string): TickerIdentity {
  const normalizedTicker = normalizeTicker(ticker);
  const curated =
    CURATED_TICKER_IDENTITIES[
      normalizedTicker as keyof typeof CURATED_TICKER_IDENTITIES
    ];

  if (curated) {
    return {
      ticker: normalizedTicker,
      ...curated,
      isCurated: true,
      ...resolveReviewedLogo(normalizedTicker),
    };
  }

  const cached = fallbackCache.get(normalizedTicker);
  if (cached) {
    return cached;
  }

  const hash = tickerHash(normalizedTicker);
  const palette = FALLBACK_PALETTES[hash % FALLBACK_PALETTES.length];
  const fallback: TickerIdentity = {
    ticker: normalizedTicker,
    symbolName: "고유 종목 문장",
    pixelPattern: createFallbackPattern(hash),
    ...palette,
    isCurated: false,
    ...resolveReviewedLogo(normalizedTicker),
  };

  fallbackCache.set(normalizedTicker, fallback);
  return fallback;
}

export function getTickerPixelColor(
  identity: TickerIdentity,
  pixel: string,
): string | null {
  if (pixel === "1") return identity.foregroundColor;
  if (pixel === "2") return identity.secondaryColor;
  if (pixel === "3") return identity.highlightColor;
  if (pixel === "4") return identity.inkColor;
  return null;
}
